import { useCallback, useMemo } from 'react';

import { Box } from '#/components/Box/Box';
import { BulletList } from '#/components/BulletList/BulletList';
import { Icon } from '#/components/Icon/Icon';
import { InfoList } from '#/components/InfoList/InfoList';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { Slideshow } from '#/components/Slideshow/Slideshow';
import { useViewportBreakpointValue } from '#/hooks/viewport/useViewportBreakpointValue';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ListingViewSection } from '#/modules/listing/components/ListingView/Section/ListingViewSection';
import {
  ListingRichTextTruncated,
  listingRichTextTruncateAtAbout8Lines,
} from '#/modules/listing/components/RichText/Truncated/ListingRichTextTruncated';
import type { ApiOrg } from '#/modules/listing/orgs/api/types/ApiOrg';
import { trackAnchorClicked } from '#/utils/analytics/track/trackAnchorClicked';
import { AREAS_OF_FOCUS } from '#/utils/constants/general/areasOfFocus';
import { showModal } from '#/zustand-stores/modalStore';

import type { ViewOrgPageListingTab } from './types/ViewOrgPageListingTab';
import { orgHasListings } from './utils/orgHasListings';
import { orgPresentListingTypes } from './utils/orgPresentListingTypes';
import { ViewOrgPageListingsTabs } from './ViewOrgPageListingsTabs';
import { ViewOrgPageListingsTabsMobile } from './ViewOrgPageListingsTabsMobile';

const SLIDESHOW_ITEM_SIZE = 200;

type Props = {
  org: ApiOrg;
};

export function ViewOrgPageDetails({ org }: Props) {
  const { i18n } = useI18n();

  const isMobileTabs = useViewportBreakpointValue({ xs: true, sm: false });

  const headerTitle = useMemo(() => {
    const hasPhoto = org.slideshowItems.find((item) => item.type === 'IMAGE');
    const hasVideo = org.slideshowItems.find((item) => item.type === 'VIDEO');

    const orgName = org.name;

    if (hasPhoto && !hasVideo) {
      return i18n('Photos for %(orgName)s', { orgName });
    }

    if (hasVideo && !hasPhoto) {
      return i18n('Videos for %(orgName)s', { orgName });
    }

    return i18n('Photos & Videos for %(orgName)s', { orgName });
  }, [i18n, org.name, org.slideshowItems]);

  const openMediaGallery = useCallback(
    ({ itemIndex }: { itemIndex: number }) => {
      showModal(
        'MEDIA_GALLERY',
        // @ts-expect-error TS(2322): Type 'SlideshowData[]' is not assignable to type '... Remove this comment to see the full error message
        { items: org.slideshowItems, itemIndex },
      );
    },
    [org.slideshowItems],
  );

  const defaultTab: ViewOrgPageListingTab = useMemo(
    () => orgPresentListingTypes(org)[0] || 'JOB',
    [org],
  );

  return (
    <Box px={0} mb={2}>
      {org.mission !== null && org.missionTruncated !== null && (
        <ListingViewSection id="mission" title={i18n('Mission')}>
          <ListingRichTextTruncated
            html={org.mission}
            truncateAtHeight={
              org.missionTruncated === org.mission
                ? null
                : listingRichTextTruncateAtAbout8Lines
            }
            showMoreLinkTitle={i18n('Read more about this organization')}
            showLessLinkTitle={i18n('Read less about this organization')}
          />
        </ListingViewSection>
      )}
      {org.description !== null && org.descriptionTruncated !== null && (
        <ListingViewSection id="about" title={i18n('About Us')}>
          <ListingRichTextTruncated
            html={org.description}
            truncateAtHeight={
              org.description === org.descriptionTruncated
                ? null
                : listingRichTextTruncateAtAbout8Lines
            }
            showMoreLinkTitle={i18n('Read more about this organization')}
            showLessLinkTitle={i18n('Read less about this organization')}
          />
        </ListingViewSection>
      )}
      {org.areasOfFocusKeys.length > 0 && (
        <ListingViewSection title={i18n('Cause Areas Include')}>
          <BulletList
            columns={2}
            itemMargin={32}
            items={org.areasOfFocusKeys.map((area) => ({
              key: area,
              value: AREAS_OF_FOCUS(i18n)[area],
            }))}
          />
        </ListingViewSection>
      )}
      <ListingViewSection
        title={
          org.phone || org.email ? i18n('Location & Contact') : i18n('Location')
        }
      >
        <InfoList $columns={1} $noSvgStyles>
          {org.phone && (
            <li>
              <Box display="inline-flex" mr="6px">
                <Icon name="cell-phone" size={24} />
              </Box>
              <StyledNativeLinkWithRef
                href={`tel:${org.phone.replace(/[^0-9+]/g, '')}`}
                onClick={(event) =>
                  trackAnchorClicked(event, 'Organization phone number', {
                    method: 'Phone',
                  })
                }
              >
                {org.phone}
              </StyledNativeLinkWithRef>
            </li>
          )}

          {org.email && (
            <li>
              <Box display="inline-flex" mr="6px">
                <Icon name="mail" size={24} />
              </Box>

              <StyledNativeLinkWithRef
                href={`mailto:${org.email}`}
                target="_blank"
                onClick={(event) =>
                  trackAnchorClicked(event, 'Organization email', {
                    method: 'Email',
                  })
                }
              >
                <Box whiteSpace="nowrap">{org.email}</Box>
              </StyledNativeLinkWithRef>
            </li>
          )}

          {org.address && (
            <li>
              <Box display="inline-flex" mr="6px">
                <Icon name="location" size={24} />
              </Box>

              <div>
                {org.address.full}
                {org.address.line2 && <br />}
                {org.address.line2}
              </div>
            </li>
          )}
        </InfoList>
      </ListingViewSection>

      {org.slideshowItems.length > 0 && (
        <ListingViewSection title={headerTitle}>
          <div
            style={
              org.slideshowItems.length === 1
                ? // Reduce width so the only item present is left aligned
                  { width: SLIDESHOW_ITEM_SIZE }
                : { margin: '0 64px' }
            }
          >
            <Slideshow
              carouselId="org-photos-carousel"
              items={org.slideshowItems}
              qaId="org-carousel"
              onClickIndex={openMediaGallery}
              size={SLIDESHOW_ITEM_SIZE}
            />
          </div>
        </ListingViewSection>
      )}

      {orgHasListings(org) && (
        <Box id="opportunities" qaId="opportunities">
          <ListingViewSection title={i18n('Current listings')}>
            {isMobileTabs ? (
              <ViewOrgPageListingsTabsMobile
                org={org}
                defaultTab={defaultTab}
              />
            ) : (
              <ViewOrgPageListingsTabs org={org} defaultTab={defaultTab} />
            )}
          </ListingViewSection>
        </Box>
      )}
    </Box>
  );
}
